import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, X, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

import { config } from '../../utils/config';
import ClubbieLoader from '../../components/Loader/ClubbieLoader';
import ClubbieModal from '../../components/Modal/ClubbieModal';
import EditPaymentModal from '../../components/EditPaymentModal/EditPaymentModal';

const PaymentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [sentReminders, setSentReminders] = useState(new Set());
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const actionsMenuRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [filters, setFilters] = useState({
    search: '',
    team: 'All teams',
    status: 'All statuses',
    sortBy: 'Name A-Z'
  });

  useEffect(() => {
    fetchPaymentDetails();
  }, [id]);

  useEffect(() => {
    applyFilters();
  }, [teamMembers, filters]);

  const fetchPaymentDetails = async () => {
    if (!payment) setLoading(true); // Only show global loading on initial fetch
    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/details/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch payment details');

      const data = await response.json();
      setPayment(data);

      if (data.paymentObjects?.length > 0) {
        const members = data.paymentObjects.map(pmt => ({
          id: pmt.paymentId,
          user: pmt.user,
          team: pmt.team,
          status: pmt.status,
          amount: pmt.amount,
          processedDate: pmt.processedDate,
          billingDate: pmt.billingDate,
        }));
        setTeamMembers(members);
        setFilteredMembers(members);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembersPayments = async (paymentData) => {
    try {
      const filter = paymentData.payFor === 'Subscription'
        ? { payReference: paymentData.payReference?.id || paymentData.payReference, payFor: 'Subscription' }
        : { customReference: paymentData.customReference, payFor: 'Custom' };

      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments?${new URLSearchParams(filter)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch team payments');

      const data = await response.json();
      const members = data.results.map(pmt => ({
        id: pmt.id,
        user: pmt.user,
        minor: pmt.minor,
        team: pmt.team,
        status: pmt.status,
        amount: pmt.amount,
        processedDate: pmt.processedDate,
        method: pmt.method
      }));

      setTeamMembers(members);
      setFilteredMembers(members);
    } catch (error) {
    }
  };

  const applyFilters = () => {
    let filtered = [...teamMembers];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(m =>
        `${m.user?.profile?.firstName} ${m.user?.profile?.lastName}`.toLowerCase().includes(search)
      );
    }

    if (filters.team !== 'All teams') {
      filtered = filtered.filter(m => m.team?.title === filters.team);
    }

    if (filters.status !== 'All statuses') {
      filtered = filtered.filter(m => m.status === filters.status);
    }

    if (filters.sortBy === 'Name A-Z') {
      filtered.sort((a, b) =>
        `${a.user?.profile?.firstName}`.localeCompare(`${b.user?.profile?.firstName}`)
      );
    } else if (filters.sortBy === 'Name Z-A') {
      filtered.sort((a, b) =>
        `${b.user?.profile?.firstName}`.localeCompare(`${a.user?.profile?.firstName}`)
      );
    }

    setFilteredMembers(filtered);
  };

  const pendingCashApprovals = useMemo(
    () => teamMembers.filter((m) => m.status === 'pending-cash-approval'),
    [teamMembers]
  );

  const acceptAllPendingCash = async () => {
    try {
      for (const member of pendingCashApprovals) {
        // eslint-disable-next-line no-await-in-loop
        await handleAcceptCash(member.id);
      }
    } catch (error) {
      toast.error('Failed to accept all cash payments. Please try again.');
    }
  };

  const sendAllReminders = async () => {
    try {
      const dueMembers = filteredMembers.filter((m) => ['unpaid', 'pending'].includes(m.status));
      for (const member of dueMembers) {
        // eslint-disable-next-line no-await-in-loop
        await sendReminder(member.id);
      }
      toast.success('Reminders sent successfully');
    } catch (error) {
      toast.error('Failed to send reminders. Please try again.');
    }
  };

  const handleAcceptCash = async (memberId) => {
    setProcessingId(memberId);
    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/${memberId}/confirm-cash-pay`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId: String(teamMembers?.find(m => m.id === memberId)?.team?.id || payment?.team || ''),
          status: 'paid'
        })
      });

      if (!response.ok) throw new Error('Failed to accept cash payment');

      await fetchPaymentDetails();
    } catch (error) {
      toast.error('Failed to accept cash payment. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectCash = (member) => {
    setSelectedMember(member);
    setShowDeclineModal(true);
  };

  const confirmRejectCash = async () => {
    setProcessingId(selectedMember.id);
    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/${selectedMember.id}/confirm-cash-pay`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId: String(selectedMember?.team?.id || payment?.team || ''),
          status: 'unpaid'
        })
      });

      if (!response.ok) throw new Error('Failed to reject cash payment');

      setShowDeclineModal(false);
      setSelectedMember(null);
      setDeclineReason('');
      await fetchPaymentDetails();
    } catch (error) {
      toast.error('Failed to reject cash payment. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const sendReminder = async (memberId) => {
    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/due-payment-notice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          paymentId: memberId
        })
      });

      if (!response.ok) throw new Error('Failed to send reminder');

      setSentReminders((prev) => new Set(prev).add(memberId));
      toast.success('Reminder sent successfully');
    } catch (error) {
      toast.error('Failed to send reminder. Please try again.');
    }
  };

  const handleDeletePayment = async () => {
    try {
      setDeleting(true);
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/delete-tournament-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          requestId: id,
          tournamentId: payment?.tournament || '',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete payment');
      }

      toast.success('Payment deleted successfully');
      navigate('/payments');
    } catch (error) {
      toast.error(error.message || 'Failed to delete payment');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', label: 'Paid' },
      'unpaid': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', label: 'Unpaid' },
      'pending': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Pending payment' },
      'pending-cash-approval': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', label: 'Pending cash approval' },
      'canceled': { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30', label: 'Canceled' }
    };

    const config = statusConfig[status] || statusConfig.unpaid;

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
        {config.label}
      </span>
    );
  };

  const calculateStats = () => {
    const totalReceived = teamMembers.filter(m => m.status === 'paid').reduce((sum, m) => sum + m.amount, 0);
    const totalPending = teamMembers.filter(m => ['unpaid', 'pending', 'pending-cash-approval'].includes(m.status)).reduce((sum, m) => sum + m.amount, 0);
    return { totalReceived, totalPending };
  };

  const parseLineItems = (customReference) => {
    if (!customReference) return [];
    const items = customReference.split(', ');
    return items.map(item => {
      const match = item.match(/^(.+):\s*£(\d+\.?\d*)$/);
      if (match) {
        return { description: match[1], amount: parseFloat(match[2]) };
      }
      return null;
    }).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000F1F] flex items-center justify-center">
        <ClubbieLoader />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="min-h-screen bg-[#000F1F] text-[#FEFCEB] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Payment not found</h2>
          <button
            onClick={() => navigate('/payments')}
            className="text-[#00E7FF] hover:text-[#00d4e6] transition-colors"
          >
            ← Back to Payments
          </button>
        </div>
      </div>
    );
  }

  const currency = payment.currency || 'GBP';
  const currencySymbol = (() => {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 })
        .format(0).replace(/\d/g, '').trim();
    } catch { return currency; }
  })();

  const isTournament = payment.payFor === 'Tournament';
  const isSubscription = payment.payFor === 'Subscription';
  const paymentItems = payment.paymentItems || [];

  const recurrenceLabel = {
    'one-time': 'One-off payment',
    'monthly': 'Monthly',
    'weekly': 'Weekly',
    'yearly': 'Yearly',
  }[payment.recurrence] || payment.recurrence || 'One-off payment';

  const billingDateFormatted = payment.createdAt
    ? new Date(payment.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'N/A';

  return (
    <div className="bg-[#000F1F] text-[#FEFCEB] p-[18px] lg:px-8 font-sans min-h-screen">
      {/* Back button */}
      <button
        onClick={() => navigate('/payments')}
        className="flex items-center gap-1 text-app-text-secondary hover:text-[#FEFCEB] transition-colors text-sm mb-4 cursor-pointer"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Back to payments
      </button>

      {/* Title */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-[22px] font-normal text-[#FEFCEB]">
          {isSubscription ? 'Monthly subscription' : payment.customReference || 'Payment Details'}
        </h1>
        <div className="relative" ref={actionsMenuRef}>
          <button
            onClick={() => setShowActionsMenu(!showActionsMenu)}
            className="p-2 text-[#7F8585] hover:text-[#FEFCEB] transition-colors cursor-pointer"
          >
            <MoreHorizontal size={20} />
          </button>

          {showActionsMenu && (
            <div className="absolute right-0 top-full mt-2 w-44 bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl z-50 overflow-hidden">
              {payment.isEditable && (
                <button
                  onClick={() => {
                    setShowActionsMenu(false);
                    setIsEditModalOpen(true);
                  }}
                  className="w-full text-left px-4 py-3 text-[#FEFCEB] hover:bg-[#0D1B29] transition-colors text-sm font-medium flex items-center gap-2 border-b border-[#192634]"
                >
                  <Pencil size={16} className="text-app-text-secondary" />
                  Edit
                </button>
              )}
              {payment.isEditable && (
                <button
                  onClick={() => {
                    setShowActionsMenu(false);
                    setShowDeleteModal(true);
                  }}
                  className="w-full text-left px-4 py-3 text-red-400 hover:bg-red-500/10 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 mb-4">
        <div className="col-span-6 flex gap-4">
          <div className="bg-app-surface rounded-2xl px-5 py-5 w-full">
            <p className="text-[#FEFCEB] text-[25px] font-semibold">{currencySymbol} {(payment.totalReceived || 0).toLocaleString()}</p>
            <p className="text-app-text-secondary text-[13px] mt-1">Total received</p>
          </div>
        </div>        
        <div className="col-span-6 flex gap-4">
          <div className="bg-app-surface rounded-2xl px-5 py-5 w-full">
            <p className="text-[#FEFCEB] text-[25px] font-semibold">{currencySymbol} {(payment.totalPending || 0).toLocaleString()}</p>
            <p className="text-app-text-secondary text-[13px] mt-1">Total pending</p>
          </div>
        </div>
      </div>
      

      {/* Main 3-column layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left column: Totals */}
        {/* <div className="col-span-2 flex flex-col gap-4">
            <div className="bg-app-surface rounded-2xl px-5 py-5 w-full">
              <p className="text-[#FEFCEB] text-[25px] font-semibold">{currencySymbol} {(payment.totalReceived || 0).toLocaleString()}</p>
              <p className="text-app-text-secondary text-[13px] mt-1">Total received</p>
            </div>
            <div className="bg-app-surface rounded-2xl px-5 py-5 w-full">
              <p className="text-[#FEFCEB] text-[25px] font-semibold">{currencySymbol} {(payment.totalPending || 0).toLocaleString()}</p>
              <p className="text-app-text-secondary text-[13px] mt-1">Total pending</p>
            </div>
        </div> */}

        {/* Middle column: General info + Payment breakdown */}
        <div className="col-span-4 flex flex-col gap-4">
          {/* General information */}
          <div className="bg-app-bg rounded-3xl border border-app-border overflow-hidden">
            <div className='bg-app-surface px-3 py-4 border-b border-app-border'>
              <h3 className="text-[13px] font-medium text-app-text-secondary">General information</h3>
            </div>
            <div className="space-y-3 px-3 py-4">
              <div className="flex justify-between text-xs">
                <span className="text-app-text-secondary">Payment due</span>
                <span className="text-[#FEFCEB]">{billingDateFormatted}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-app-text-secondary">Recurrence</span>
                <span className="text-[#FEFCEB]">{recurrenceLabel}</span>
              </div>
            </div>
          </div>

          {/* Payment breakdown */}
          {paymentItems.length > 0 && (
            <div className="bg-app-bg rounded-3xl border border-app-border overflow-hidden">
              <div className='bg-app-surface px-3 py-4 border-b border-app-border'>
                <h3 className="text-[13px] font-medium text-app-text-secondary">Payment breakdown</h3>
              </div>
              <div className="space-y-3 px-3 py-4">
                {paymentItems.map((item, index) => (
                  <div key={index} className="flex justify-between text-xs">
                    <span className="text-app-text-secondary">{item.description}</span>
                    <span className="text-[#FEFCEB]">{currencySymbol} {parseFloat(item.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
                {payment.feePayer && (
                  <>
                    <div className="flex justify-between text-[13px] font-medium">
                      <span className="text-[#FEFCEB]">Total</span>
                      <span className="text-[#FEFCEB]">{currencySymbol} {(payment.amount || 0).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column: Team members */}
        <div className="col-span-8">

          <div className="bg-app-bg rounded-3xl border border-app-border overflow-hidden">
            <div className='bg-app-surface px-3 py-4 border-b border-app-border'>
              <h3 className="text-[13px] font-medium text-app-text-secondary">Teams</h3>
            </div>
            {/* Pending cash approvals */}
            {pendingCashApprovals.length > 0 && (
              <div className="">
                <div className="flex bg-app-surface items-center justify-between px-3 py-4 border-b border-app-border">
                  <span className="text-app-text-secondary text-xs">Pending cash approval</span>
                  <button onClick={acceptAllPendingCash} className="text-app-text-secondary hover:text-[#FEFCEB] text-xs transition-colors underline">Accept all</button>
                </div>
                <div className="">
                  {pendingCashApprovals.map((member) => (
                    <div key={member.id} className="flex items-center justify-between px-3 py-3 border-b border-app-border">
                      <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-[#011224] flex items-center justify-center text-[#FEFCEB] text-xs font-semibold border border-app-border overflow-hidden">
                          {member.team?.crest ? (
                          <img src={member.team.crest} alt="" className="w-full h-full object-cover" />
                          ) : (
                            member.team?.title?.[0] || '?'
                          )}
                        </div>
                        <span className="text-sm text-[#FEFCEB]">
                          {member.team?.title || 'Unknown Team'}
                          {member.user?.fullName && <span className="text-app-text-secondary text-xs ml-1">({member.user.fullName})</span>}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleAcceptCash(member.id)} className="p-2 text-green-400 hover:text-green-300" disabled={processingId === member.id}>
                          <Check size={20} />
                        </button>
                        <button onClick={() => handleRejectCash(member)} className="p-2 text-red-400 hover:text-red-300" disabled={processingId === member.id}>
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment status list */}
            <div>
              <div className="flex bg-app-surface items-center justify-between px-3 py-4 border-b border-app-border">
                <span className="text-app-text-secondary text-xs">Payment status</span>
                <button onClick={sendAllReminders} className="text-app-text-secondary hover:text-[#FEFCEB] text-xs transition-colors underline">Send all reminders</button>
              </div>
              <div className="">
                {filteredMembers.filter((m) => m.status !== 'pending-cash-approval').map((member) => (
                  <div key={member.id} className="flex items-center justify-between border-b border-app-border px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-full bg-[#011224] flex items-center justify-center text-[#FEFCEB] text-xs font-semibold border border-app-border overflow-hidden">
                        {member.team?.crest ? (
                          <img src={member.team.crest} alt="" className="w-full h-full object-cover" />
                        ) : (
                          member.team?.title?.[0] || '?'
                        )}
                      </div>
                      <div className='flex flex-col'>
                        <span className="text-sm text-[#FEFCEB]">
                          {member.team?.title || 'Unknown Team'}
                          {member.user?.fullName && <span className="text-app-text-secondary text-xs ml-1">({member.user.fullName})</span>}
                        </span>
                        <span className={`text-xs ${
                          member.status === 'paid' ? 'text-app-accent-green'
                          : (member.billingDate && new Date(member.billingDate) < new Date()) ? 'text-app-error'
                          : 'text-yellow-400'
                        }`}>
                          {member.status === 'paid' ? 'Paid'
                          : (member.billingDate && new Date(member.billingDate) < new Date()) ? 'Overdue'
                          : 'Pending'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {member.status === 'paid' && member.processedDate && (
                        <span className="text-xs text-app-text-secondary">
                          {new Date(member.processedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      {['unpaid', 'pending'].includes(member.status) && (
                        sentReminders.has(member.id) ? (
                          <span className="text-xs text-app-text-secondary">Reminder sent</span>
                        ) : (
                          <button onClick={() => sendReminder(member.id)} className="text-xs text-[#FEFCEB] font-medium hover:text-[#00E7FF] transition-colors underline">
                            Send reminder
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
                {filteredMembers.length === 0 && (
                  <p className="text-app-text-secondary text-sm py-4 text-center">No members found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ClubbieModal
        isOpen={showDeclineModal}
        toggle={() => { setShowDeclineModal(false); setSelectedMember(null); }}
        title={`Decline payment from ${selectedMember?.user?.fullName || `${selectedMember?.user?.firstName || ''} ${selectedMember?.user?.secondName || ''}`}`}
        width="500px"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <button
              type="button"
              className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
              onClick={() => { setShowDeclineModal(false); setSelectedMember(null); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-6 py-3 rounded-full bg-app-error hover:bg-red-600 text-blue-shade-8 text-sm font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={confirmRejectCash}
              disabled={!!processingId}
            >
              {processingId ? 'Declining...' : 'Decline'}
            </button>
          </div>
        }
      >
        <p className="text-app-text-secondary text-[13px]">
          Are you sure you want to decline this payment? This action cannot be undone.
        </p>
      </ClubbieModal>

      <ClubbieModal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(false)}
        title="Delete payment"
        width="500px"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <button
              type="button"
              className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-6 py-3 rounded-full bg-app-error hover:bg-red-600 text-blue-shade-8 text-sm font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleDeletePayment}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        }
      >
        <p className="text-app-text-secondary text-[13px]">
          Are you sure you want to delete this payment? This will remove all payment requests for all teams. This action cannot be undone.
        </p>
      </ClubbieModal>

      <EditPaymentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => fetchPaymentDetails()}
        requestId={id}
      />
    </div>
  );
};

export default PaymentDetails;
